import { useState, useEffect } from "react";

export interface DeadlineCountdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  isUrgent: boolean; // < 1 hour remaining
  isWarning: boolean; // < 24 hours remaining
  totalMilliseconds: number;
}

export function useDeadlineCountdown(
  deadline: string | null | undefined
): DeadlineCountdown | null {
  const [timeLeft, setTimeLeft] = useState<DeadlineCountdown | null>(null);

  useEffect(() => {
    if (!deadline) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const now = Date.now();
      const deadlineTime = new Date(deadline).getTime();
      const diff = deadlineTime - now;

      if (diff <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
          isUrgent: false,
          isWarning: false,
          totalMilliseconds: 0,
        });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const isUrgent = diff < 1000 * 60 * 60; // < 1 hour
      const isWarning = diff < 1000 * 60 * 60 * 24; // < 24 hours

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
        isExpired: false,
        isUrgent,
        isWarning,
        totalMilliseconds: diff,
      });
    };

    calculateTimeLeft();

    // Update more frequently when deadline is closer
    const getInterval = () => {
      const now = Date.now();
      const deadlineTime = new Date(deadline).getTime();
      const diff = deadlineTime - now;

      if (diff < 1000 * 60 * 60) return 1000; // < 1 hour: every second
      if (diff < 1000 * 60 * 60 * 24) return 60000; // < 24 hours: every minute
      return 60000 * 5; // > 24 hours: every 5 minutes
    };

    let intervalId: ReturnType<typeof setInterval>;

    const scheduleUpdate = () => {
      clearInterval(intervalId);
      intervalId = setInterval(() => {
        calculateTimeLeft();
        scheduleUpdate(); // Reschedule with potentially different interval
      }, getInterval());
    };

    scheduleUpdate();

    return () => clearInterval(intervalId);
  }, [deadline]);

  return timeLeft;
}

// Helper function to format countdown for display
export function formatCountdown(
  countdown: DeadlineCountdown | null,
  options: {
    expiredText?: string;
    daysText?: (days: number) => string;
    hoursText?: (hours: number) => string;
    minutesText?: (minutes: number) => string;
    secondsText?: (seconds: number) => string;
  } = {}
): string | null {
  if (!countdown) return null;

  const {
    expiredText = "Expired",
    daysText = (d) => `${d}d`,
    hoursText = (h) => `${h}h`,
    minutesText = (m) => `${m}m`,
    secondsText = (s) => `${s}s`,
  } = options;

  if (countdown.isExpired) return expiredText;

  if (countdown.days > 0) {
    return `${daysText(countdown.days)} ${hoursText(countdown.hours)}`;
  }

  if (countdown.hours > 0) {
    return `${hoursText(countdown.hours)} ${minutesText(countdown.minutes)}`;
  }

  if (countdown.minutes > 0) {
    return `${minutesText(countdown.minutes)} ${secondsText(countdown.seconds)}`;
  }

  return secondsText(countdown.seconds);
}
